import { useTranslation } from 'react-i18next'
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom'

import { buttonClasses } from '@/design-system/Button'
import { Badge, Card, Container, Overline, Skeleton } from '@/design-system/primitives'
import { usePublicEvent } from '@/features/event/api'
import { AddFoodLink } from '@/features/food/components'
import { formatDateTime } from '@/lib/datetime'
import { formatMoney } from '@/lib/money'

import { CLOSED_ORDER_STATUSES, useOrderStatus } from '../api'
import { SupplementCard } from '../SupplementCard'
import type { PaymentIntent } from '../types'

/**
 * Rend cliquable les adresses contenues dans les consignes de paiement.
 *
 * L'équipe y écrit un lien de règlement — PayPal, virement, peu importe. En
 * texte brut, il faudrait le recopier à la main depuis un téléphone : personne
 * ne le fait, et la commande reste impayée.
 *
 * Le découpage se fait sur le texte, jamais sur du HTML injecté : la consigne
 * est saisie en back-office, et une chaîne saisie ne doit jamais pouvoir
 * devenir du balisage exécuté.
 */
function Linkified({ text }: { text: string }) {
  const parts = text.split(/(https?:\/\/[^\s]+)/g)
  return (
    <>
      {parts.map((part, index) =>
        /^https?:\/\//.test(part) ? (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noreferrer noopener"
            className="break-all text-ember-400 underline underline-offset-2 hover:text-ember-300"
          >
            {part}
          </a>
        ) : (
          part
        ),
      )}
    </>
  )
}

const STATUS_LABELS: Record<string, string> = {
  pending_payment: 'order.pending',
  paid: 'order.paid',
  expired: 'order.expired',
  cancelled: 'order.cancelled',
  refunded: 'order.refunded',
  partially_refunded: 'order.partiallyRefunded',
}

/** Ce qu'on dit à quelqu'un dont la commande ne se réglera plus. */
const CLOSED_MESSAGES: Record<string, string> = {
  cancelled: 'order.closedCancelled',
  expired: 'order.closedExpired',
  refunded: 'order.closedRefunded',
  partially_refunded: 'order.closedPartiallyRefunded',
}

/**
 * Suivi de commande.
 *
 * Le statut affiché est celui que le **serveur** constate. Le navigateur ne
 * conclut jamais qu'un paiement a réussi : il interroge cet endpoint jusqu'à
 * ce que le règlement soit confirmé — par webhook signé, ou par un membre de
 * l'équipe qui a encaissé.
 */
export function OrderPage() {
  const { t, i18n } = useTranslation()
  const { orderId } = useParams<{ orderId: string }>()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  // `access` au retour de PayPal : PayPal ajoute lui-même un `token` (le sien)
  // à l'adresse de retour, qui aurait pris la place du nôtre.
  const token = searchParams.get('access') ?? searchParams.get('token')

  // À la création, l'intention de paiement arrive par l'état de navigation.
  // Elle ne survivait pas à un rechargement : le serveur la renvoie désormais
  // avec le statut (consignes, référence, lien), et c'est elle qui prime.
  const navigationPayment =
    (location.state as { payment?: PaymentIntent } | null)?.payment ?? null

  const statusQuery = useOrderStatus(orderId, token)
  const eventQuery = usePublicEvent()
  const timeZone = eventQuery.data?.timezone ?? 'Europe/Berlin'

  // Sans jeton, la requête ne part jamais : elle restait « en attente » et la
  // page affichait un squelette sans fin. Un lien tronqué mène à « introuvable ».
  if (token && statusQuery.isPending) {
    return (
      <Container className="py-32">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="mt-6 h-32 w-full max-w-xl" />
      </Container>
    )
  }

  if (!token || statusQuery.isError || !statusQuery.data) {
    return (
      <Container className="py-32">
        <h1 className="font-display text-display-lg text-content-primary">
          {t('order.notFound')}
        </h1>
        <Link to="/" className="mt-6 inline-block text-champagne-300 underline">
          {t('order.backHome')}
        </Link>
      </Container>
    )
  }

  const order = statusQuery.data
  // Ouverte avec le jeton d'un lot de plats : la page ne montre que lui,
  // sous le numéro de la commande — ni le QR, ni les invités, ni la table.
  const supplementScope = order.scope === 'supplement'
  const supplements = supplementScope ? [] : (order.supplements ?? [])
  const supplementsDue = supplementScope ? 0 : (order.supplements_due_minor ?? 0)
  const statusKey = STATUS_LABELS[order.status] ?? 'order.pending'
  // Une commande annulée, expirée ou remboursée ne se paie plus : afficher
  // encore la référence et le lien, c'était inviter à payer pour rien.
  const closed = !order.paid && CLOSED_ORDER_STATUSES.has(order.status)
  const payment = order.payment ?? navigationPayment
  const money = (minor: number) =>
    formatMoney({ amount_minor: minor, currency: order.currency }, i18n.language)
  const when = (iso: string) => formatDateTime(iso, i18n.language, timeZone)
  const pending = (order.installments ?? []).filter((row) => row.status === 'pending')
  // Le lien de règlement en bouton : le recopier depuis un téléphone, personne
  // ne le fait. Absent pour qui règle à l'entrée.
  const payUrl = order.pays_at_door ? null : (order.payment_link ?? payment?.redirect_url ?? null)
  // Ce que contient la commande. « Vos billets sont émis » à qui a commandé
  // trois plats était faux : la page nomme ce qui a réellement été acheté.
  const contents = order.contents ?? null
  const families = contents
    ? (
        [
          ['tickets', contents.tickets],
          ['table', contents.table],
          ['food', contents.food],
          ['bottles', contents.bottles],
        ] as const
      )
        .filter(([, present]) => present)
        .map(([family]) => t(`orderContents.${family}`))
    : []
  // Une entrée (billet ou table) permet d'y ajouter à manger — une fois
  // tenue : réglée, à régler à l'entrée, ou avec un acompte. Le serveur
  // refuse une entrée encore en attente de son paiement en ligne ; proposer
  // le bouton pour le refuser ensuite serait une fausse promesse.
  const entryHeld = order.paid || Boolean(order.pays_at_door) || (order.paid_minor ?? 0) > 0
  const canAddFood = Boolean(
    !supplementScope &&
      contents &&
      (contents.admission || contents.table) &&
      !closed &&
      entryHeld,
  )

  return (
    <Container className="py-32">
      <Overline>{t('ticketing.title')}</Overline>
      <h1 className="mt-3 font-display text-display-lg text-content-primary">
        {supplementScope ? t('supplement.pageTitle') : t('order.title')}
      </h1>

      {/* Le même numéro que la réservation : c'est la promesse faite au client
          (« un numéro, un QR »), elle se dit avant tout le reste. */}
      {supplementScope ? (
        <p className="mt-6 max-w-xl rounded-lg border border-champagne-400/40 bg-champagne-400/5 px-5 py-4 text-sm text-champagne-300">
          {t('supplement.scopeNotice', { number: order.order_number })}
        </p>
      ) : null}

      {/* Une ancienne commande de plats, regroupée dans la réservation. */}
      {order.merged_into_order_number ? (
        <p className="mt-6 max-w-xl rounded-lg border border-champagne-400/40 bg-champagne-400/5 px-5 py-4 text-sm text-champagne-300">
          {t('supplement.mergedInto', { number: order.merged_into_order_number })}{' '}
          {order.merged_into_order_id && order.merged_access_token ? (
            <Link
              to={`/commande/${order.merged_into_order_id}?access=${encodeURIComponent(order.merged_access_token)}`}
              className="text-champagne-300 underline"
            >
              {t('supplement.openMerged', { number: order.merged_into_order_number })}
            </Link>
          ) : null}
        </p>
      ) : null}

      <Card className="mt-10 max-w-xl space-y-5">
        <div className="flex items-baseline justify-between gap-6">
          <span className="overline text-content-muted">{t('order.number')}</span>
          <span className="font-display text-heading-md tabular-nums text-content-primary">
            {order.order_number}
          </span>
        </div>

        <div className="flex items-center justify-between gap-6">
          <span className="overline text-content-muted">{t('order.status')}</span>
          <Badge tone={order.paid ? 'success' : closed ? 'neutral' : 'ember'}>
            {t(statusKey)}
          </Badge>
        </div>

        {families.length > 0 ? (
          <div className="flex items-baseline justify-between gap-6">
            <span className="overline shrink-0 text-content-muted">
              {t('orderContents.label')}
            </span>
            <span className="text-right text-sm text-content-primary">
              {families.join(' · ')}
            </span>
          </div>
        ) : null}

        {/* Des plats seuls ne font entrer personne : le dire ici évite de se
            présenter à la porte avec une confirmation de repas. */}
        {contents && !contents.admission && !contents.table && !supplementScope ? (
          <p className="text-sm text-content-secondary">
            {contents.admission_order_number
              ? t('orderContents.linked', { number: contents.admission_order_number })
              : t('orderContents.noAccess')}
          </p>
        ) : null}

        <div className="flex items-baseline justify-between gap-6 border-t border-line-subtle pt-5">
          <span className="overline text-content-muted">
            {supplementScope ? t('supplement.amount') : t('ticketing.total')}
          </span>
          <span className="font-display text-heading-lg tabular-nums text-champagne-300">
            {formatMoney(
              { amount_minor: order.total_minor, currency: order.currency },
              i18n.language,
            )}
          </span>
        </div>

        {/* Des plats ajoutés ensuite : la réservation coûte la somme des deux,
            et ce qui reste dû se lit d'un coup d'œil — sans faire passer une
            table réglée pour « non payée ». */}
        {supplements.some((row) => !['cancelled', 'expired', 'refunded'].includes(row.status)) ? (
          <div className="flex items-baseline justify-between gap-6">
            <span className="overline text-content-muted">{t('supplement.grandTotal')}</span>
            <span className="font-display text-heading-md tabular-nums text-content-primary">
              {money(order.grand_total_minor ?? order.total_minor)}
            </span>
          </div>
        ) : null}
        {supplementsDue > 0 ? (
          <p className="text-sm font-medium text-ember-300">
            {t('supplement.due', { amount: money(supplementsDue) })}
          </p>
        ) : null}
      </Card>

      {supplements.length > 0 ? (
        <section className="mt-10 max-w-xl">
          <p className="overline text-content-muted">{t('supplement.sectionTitle')}</p>
          <p className="mt-1 text-sm text-content-secondary">{t('supplement.qrSame')}</p>
          <div className="mt-4 space-y-4">
            {supplements.map((row) => (
              <SupplementCard key={row.id} supplement={row} timeZone={timeZone} />
            ))}
          </div>
        </section>
      ) : null}

      {order.paid ? (
        <p className="mt-8 max-w-xl text-state-success">
          {supplementScope
            ? t('supplement.paidNotice')
            : contents && !contents.admission
              ? t('payment.confirmedOrder')
              : t('payment.confirmed')}
        </p>
      ) : closed ? (
        <p className="mt-8 max-w-xl text-content-secondary">
          {supplementScope
            ? t('supplement.closed', { number: order.order_number })
            : t(CLOSED_MESSAGES[order.status] ?? 'order.closedCancelled')}
        </p>
      ) : (
        <div className="mt-8 max-w-xl space-y-4">
          <p className="overline text-content-muted">{t('payment.title')}</p>

          {/* La référence d'abord, en gros et détachée du texte : c'est la
              seule chose que le client doit recopier, et l'oublier rend son
              virement impossible à rattacher à cette commande. */}
          {order.payment_reference ? (
            <div className="rounded-lg border border-ember-500/40 bg-ember-500/10 px-6 py-4">
              <p className="overline text-content-muted">{t('payment.referenceLabel')}</p>
              <p className="mt-1 select-all font-display text-heading-lg tracking-wide text-ember-300">
                {order.payment_reference}
              </p>
              <p className="mt-1 text-xs text-content-muted">{t('payment.referenceHint')}</p>
            </div>
          ) : null}

          {/* Ce qui est dû maintenant, et pour quand — avec l'échéancier quand
              il y a plusieurs tranches. Le total seul faisait payer d'un coup
              ce que le client avait choisi d'étaler. */}
          {order.amount_due_now_minor != null ? (
            <div className="rounded-lg border border-line-subtle px-6 py-4 text-sm">
              <p className="text-content-primary">
                {t('order.dueNow', { amount: money(order.amount_due_now_minor) })}
                {pending[0] && !order.pays_at_door
                  ? ` ${t('order.dueBy', { date: when(pending[0].due_at) })}`
                  : ''}
              </p>
              {pending.length > 1 ? (
                <ul className="mt-3 space-y-1 text-content-secondary">
                  {pending.map((row) => (
                    <li key={row.sequence} className="flex justify-between gap-4">
                      <span>
                        {t('order.instalment', { index: row.sequence, date: when(row.due_at) })}
                      </span>
                      <span className="tabular-nums">{money(row.amount_minor)}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
              {/* Une commande de dernière minute a une échéance immédiate : la
                  tâche de nuit l'annule environ un jour plus tard si rien
                  n'est arrivé. La règle reste — elle se dit. */}
              {order.cancel_after ? (
                <p className="mt-3 text-caption text-state-warning">
                  {supplementScope
                    ? t('supplement.cancelAfter', { date: when(order.cancel_after) })
                    : t('order.cancelAfter', { date: when(order.cancel_after) })}
                </p>
              ) : null}
            </div>
          ) : null}

          {payUrl ? (
            <a
              href={payUrl}
              target="_blank"
              rel="noreferrer noopener"
              className={buttonClasses({ size: 'md' })}
            >
              {t('order.payNow')}
            </a>
          ) : null}

          {payment?.instructions ? (
            <div className="glass rounded-lg px-6 py-5">
              <p className="text-sm text-content-primary">{t('payment.manualTitle')}</p>
              <p className="mt-2 whitespace-pre-line text-sm text-content-secondary">
                <Linkified text={payment.instructions} />
              </p>
            </div>
          ) : (
            <p className="text-content-secondary">
              {payment?.client_secret ? t('payment.cardPending') : t('payment.manualDefault')}
            </p>
          )}

          {/* La page s'actualise seule : c'est le serveur qui tranche. */}
          <p className="text-sm text-content-muted" aria-live="polite">
            {t('payment.waiting')}
          </p>
        </div>
      )}

      <p className="mt-6 max-w-xl text-sm text-content-muted">{t('order.keepLink')}</p>

      {/* « Mon Masked Night » s'ouvre avec le même jeton que cette page : on
          y retrouve billets, QR et commandes, depuis n'importe quel appareil.
          Le jeton d'un lot de plats ne l'ouvre pas : le lien complet est
          parti dans la boîte du titulaire de la réservation. */}
      {supplementScope ? (
        <p className="mt-4 max-w-xl text-sm text-content-muted">{t('supplement.fullLinkByEmail')}</p>
      ) : token ? (
        <Link
          to={`/mon-billet?token=${encodeURIComponent(token)}`}
          className="mt-4 block w-fit text-champagne-300 underline"
        >
          {t('order.myTickets')}
        </Link>
      ) : null}

      {canAddFood ? (
        <div className="mt-8 max-w-xl">
          <AddFoodLink orderNumber={order.order_number} />
        </div>
      ) : null}


      <Link to="/" className="mt-10 block w-fit text-champagne-300 underline">
        {t('order.backHome')}
      </Link>
    </Container>
  )
}
